import { buildStage, type Stage } from "./scene";

/** 3x 端末をそのまま扱うと画素数が 9 倍になるので上限を設ける。 */
const MAX_DPR = 2;
/** reduced-motion で 1 枚だけ描くときのポーズ。登場が終わって漂っている頃合い。 */
const SETTLED_TIME = 4.2;

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

let stage: Stage | null = null;
let raf = 0;
let time = 0;
let last = 0;
let generation = 0;
let lastW = 0;
let lastH = 0;
let lastDpr = 0;

const pointer: [number, number] = [0, 0];
const target: [number, number] = [0, 0];

function fitStage() {
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (w === lastW && h === lastH && dpr === lastDpr) return;
  lastW = w;
  lastH = h;
  lastDpr = dpr;
  stage?.resize(w, h, dpr);
}

function tick(now: number) {
  raf = requestAnimationFrame(tick);
  if (!stage) return;

  // タブ復帰直後に巨大な dt が入ると登場アニメが飛ぶので頭を押さえる。
  const dt = Math.min((now - last) / 1000, 1 / 20);
  last = now;
  time += dt;

  fitStage();

  // 指を離した後はゆっくり中央へ戻す。放置された画面が偏り続けるのを避ける。
  pointer[0] += (target[0] - pointer[0]) * Math.min(1, dt * 4);
  pointer[1] += (target[1] - pointer[1]) * Math.min(1, dt * 4);

  if (reduced.matches) {
    stage.render({ t: SETTLED_TIME, pointer: [0, 0], settled: true });
    cancelAnimationFrame(raf);
    raf = 0;
    return;
  }
  stage.render({ t: time, pointer, settled: false });
}

async function boot() {
  const mine = ++generation;
  let built: Stage;
  try {
    built = await buildStage(canvas);
  } catch (err) {
    // WebGL2 が無い・初期化に失敗した場合は h1 の素の文字に任せる。
    document.documentElement.dataset.gl = "unsupported";
    console.error(err);
    return;
  }
  // 準備中にもう一度 context を失っていたら、この結果は捨てる。
  if (mine !== generation) {
    built.dispose();
    return;
  }
  stage = built;
  document.documentElement.dataset.gl = "ready";
  lastW = lastH = lastDpr = 0;
  fitStage();
  last = performance.now();
  if (!raf) raf = requestAnimationFrame(tick);
}

canvas.addEventListener("webglcontextlost", (e) => {
  // 既定では復帰イベントが来ないので明示的に止める。
  e.preventDefault();
  generation++;
  stage?.dispose();
  stage = null;
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
});
canvas.addEventListener("webglcontextrestored", () => void boot());

// 見えていない間は回さない。背景タブでの電池と発熱を抑える。
document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  } else if (!raf && stage) {
    last = performance.now();
    raf = requestAnimationFrame(tick);
  }
});

const setTarget = (x: number, y: number) => {
  const s = Math.min(canvas.clientWidth, canvas.clientHeight);
  target[0] = (x - canvas.clientWidth / 2) / s;
  target[1] = (y - canvas.clientHeight / 2) / s;
};
window.addEventListener("pointermove", (e) => setTarget(e.clientX, e.clientY), { passive: true });
window.addEventListener("pointerdown", (e) => setTarget(e.clientX, e.clientY), { passive: true });
window.addEventListener("pointerleave", () => {
  target[0] = 0;
  target[1] = 0;
});
reduced.addEventListener("change", () => {
  if (!raf && stage) {
    last = performance.now();
    raf = requestAnimationFrame(tick);
  }
});

void boot();
